/**
 * §B explicit-save wiring — owns the Voice Profile buffer and registers it
 * with the page's per-letter save registry (components/brand-bible/save).
 *
 * §B is 100% v2-homed: every edit lands in bb_voice_profile or bb_chunks
 * through the wizard store's existing `upsertEntry` / `discardEntry`, so this
 * hook deliberately does NOT use `useBbDraftCommit()` — §B owns no flat
 * brand_bible columns and contributes no entries to bbLegacyFieldOwnership.
 * (`tone_of_writing` is NOT §B's: it is already excluded from the v2 payload
 * by §7.6 and stays fully editable through the legacy path for non-v2
 * tenants, so claiming ownership of it would strip a live write path.)
 *
 * Commit order in save(): deletes → edits → creates → attributes → scalars →
 * queued verifies. Each step's failure keeps only ITS OWN ops buffered, so a
 * retry never redoes work that already landed, and §B stays dirty exactly as
 * the registry's partial-failure contract expects.
 *
 * Server truth is read through `useBrandBibleWizardStore.getState()` inside
 * commit, never captured at render — that is what makes the attribute
 * snapshot-then-diff honest, and it keeps the whole hook ref-free.
 */
import { useCallback, useMemo, useState } from 'react';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import { useSingleton } from '@/components/brand-bible-wizard/steps/useSingleton';
import { useRegisterBbSectionSave, type DirtyFormSaveResult } from '@/components/brand-bible/save';
import type { BbEntry, ChunkPayload } from '@/features/brand-bible-wizard/types';
import {
  addAttribute,
  addRow,
  draftPayload,
  editRow,
  emptyVoiceBuffer,
  hasAttributeOps,
  isVoiceBufferDirty,
  mergeRows,
  queueVerifyAfterSave,
  removeAttribute,
  removeRow,
  resolveAttributes,
  scalarValue,
  setScalar,
  voiceBufferChangeCount,
  type NewChunkDraft,
  type VoiceBuffer,
  type VoiceScalarField,
} from './voiceProfileBuffer';

const reason = (err: unknown, what: string): string =>
  `${what}: ${err instanceof Error ? err.message : String(err)}`;

/** Server truth at COMMIT time, not render time. */
const currentSectionBChunks = (): BbEntry<'chunks'>[] =>
  ((useBrandBibleWizardStore.getState().entries.chunks ?? []) as BbEntry<'chunks'>[]).filter(
    (c) => c.deleted_at === null && c.section === 'B',
  );

const currentVoiceEntry = (): BbEntry<'voice_profile'> | undefined =>
  ((useBrandBibleWizardStore.getState().entries.voice_profile ?? []) as BbEntry<'voice_profile'>[]).find(
    (e) => e.location_scope === 'all' && e.deleted_at === null,
  );

export function useVoiceProfileSave(legacyToneFallback?: string) {
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const voice = useSingleton('voice_profile', bbTenant);
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  const storedChunks = useBrandBibleWizardStore(selectEntries('chunks')).filter(
    (c) => c.deleted_at === null && c.section === 'B',
  );

  const [buffer, setBuffer] = useState<VoiceBuffer>(emptyVoiceBuffer);

  // ---- edit actions (all synchronous; nothing touches the network) --------

  const storedScalar = (key: VoiceScalarField): string | undefined => {
    if (key === 'voice_summary') return voice.entry?.voice_summary ?? legacyToneFallback ?? '';
    return voice.entry?.[key] ?? '';
  };

  const actions = useMemo(
    () => ({
      setField: (key: VoiceScalarField, value: string, stored: string | undefined) =>
        setBuffer((b) => setScalar(b, key, value, stored)),
      addAttr: (value: string) => setBuffer((b) => addAttribute(b, value)),
      removeAttr: (value: string) => setBuffer((b) => removeAttribute(b, value)),
      addRow: (draft: Omit<NewChunkDraft, 'tempId'>) => setBuffer((b) => addRow(b, draft)),
      removeRow: (id: string) => setBuffer((b) => removeRow(b, id)),
      editRow: (id: string, patch: Partial<ChunkPayload>) =>
        setBuffer((b) => editRow(b, id, patch)),
      reclassifyToRejected: (id: string) =>
        setBuffer((b) => queueVerifyAfterSave(editRow(b, id, { kind: 'rejected_example' }), id)),
    }),
    [],
  );

  // ---- views (buffer applied over store truth) ---------------------------

  const attributes = useMemo(
    () => resolveAttributes(voice.entry?.attributes ?? [], buffer),
    [voice.entry?.attributes, buffer],
  );
  const doSay = useMemo(() => mergeRows(storedChunks, buffer, 'do_say'), [storedChunks, buffer]);
  const neverSay = useMemo(
    () => mergeRows(storedChunks, buffer, 'never_say'),
    [storedChunks, buffer],
  );
  const formattingRows = useMemo(
    () => mergeRows(storedChunks, buffer, 'formatting_rule'),
    [storedChunks, buffer],
  );
  const golden = useMemo(
    () => mergeRows(storedChunks, buffer, 'golden_example'),
    [storedChunks, buffer],
  );
  const rejected = useMemo(
    () => mergeRows(storedChunks, buffer, 'rejected_example'),
    [storedChunks, buffer],
  );

  // ---- registry contract -------------------------------------------------

  const discard = useCallback(() => setBuffer(emptyVoiceBuffer()), []);

  const commit = useCallback(
    async (b: VoiceBuffer): Promise<DirtyFormSaveResult> => {
      const errors: string[] = [];
      const kept = emptyVoiceBuffer();

      // 1 — deletes. ID-KEYED, replayed exactly as clicked; never derived from
      // a list comparison. A row already gone (another writer got there first)
      // is skipped to save a round trip — discardEntry treats 404 as success
      // anyway, so this is an optimization, not a safety net.
      for (const id of b.rowDel) {
        if (!currentSectionBChunks().some((c) => c.id === id)) continue;
        try {
          await discardEntry('chunks', id, bbTenant);
        } catch (err) {
          errors.push(reason(err, 'remove'));
          kept.rowDel.push(id);
        }
      }

      // 2 — edits on persisted rows.
      for (const [id, patch] of Object.entries(b.rowEdit)) {
        try {
          await upsertEntry('chunks', patch, id, bbTenant);
        } catch (err) {
          errors.push(reason(err, 'edit'));
          kept.rowEdit[id] = patch;
        }
      }

      // 3 — creates. List-table create keys are per-attempt in the store, so N
      // buffered adds can never coalesce into one row.
      for (const draft of b.rowNew) {
        try {
          await upsertEntry('chunks', draftPayload(draft, 'B'), undefined, bbTenant);
        } catch (err) {
          errors.push(reason(err, 'add'));
          kept.rowNew.push(draft);
        }
      }

      // 4 — attributes: SNAPSHOT-THEN-DIFF. The list is read here, at commit
      // time, not when the chip was clicked — so a value another writer added
      // while this buffer was open survives instead of being reverted.
      if (hasAttributeOps(b)) {
        const entry = currentVoiceEntry();
        try {
          await upsertEntry('voice_profile', { attributes: resolveAttributes(entry?.attributes ?? [], b) }, entry?.id, bbTenant);
        } catch (err) {
          errors.push(reason(err, 'attributes'));
          kept.attrAdd = [...b.attrAdd];
          kept.attrDel = [...b.attrDel];
        }
      }

      // 5 — scalars: only the keys the operator actually touched, so an
      // untouched foreign `person` value is never coerced or cleared.
      if (Object.keys(b.fields).length > 0) {
        try {
          await upsertEntry('voice_profile', { ...b.fields }, currentVoiceEntry()?.id, bbTenant);
        } catch (err) {
          errors.push(reason(err, 'voice fields'));
          kept.fields = { ...b.fields };
        }
      }

      // 6 — queued verifies (reclassify). Skipped while the row's own edit is
      // still unsaved; a failed verify is reported but not re-queued — Verify
      // is an immediate control the operator can simply click again.
      for (const id of b.verifyAfterSave) {
        if (kept.rowEdit[id]) {
          kept.verifyAfterSave.push(id);
          continue;
        }
        try {
          await verifyEntry('chunks', id, undefined, bbTenant);
        } catch (err) {
          errors.push(reason(err, 'verify'));
        }
      }

      setBuffer(kept);
      return errors.length > 0 ? { success: false, error: errors.join(' · ') } : true;
    },
    [discardEntry, upsertEntry, verifyEntry, bbTenant],
  );

  // New identity on every buffer change so the registry re-notifies its
  // subscribers — that is how the page's Save button and Unsaved indicator
  // learn §B went dirty (register() calls notify()). The closure also pins
  // THIS render's buffer, so save() can never commit a stale one.
  const registration = useMemo(
    () => ({
      isDirty: () => isVoiceBufferDirty(buffer),
      changeCount: () => voiceBufferChangeCount(buffer),
      save: () => commit(buffer),
      discard,
    }),
    [buffer, commit, discard],
  );
  useRegisterBbSectionSave('B', registration);

  return {
    voice,
    verifyEntry,
    bbTenant,
    buffer,
    actions,
    storedScalar,
    scalar: (key: VoiceScalarField) => scalarValue(buffer, key, storedScalar(key)),
    attributes,
    doSay,
    neverSay,
    formattingRows,
    golden,
    rejected,
    changeCount: voiceBufferChangeCount(buffer),
  };
}
